import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, Check, Plus, MapPin } from 'lucide-react';
import { useAuth } from '../lib/AuthContext';

const STATUS_COLOR = {
    active: 'var(--neon-teal)',
    trial: 'var(--neon-cyan)',
    pending_payment: 'var(--amber)',
    suspended: 'var(--neon-magenta)',
    expired: 'var(--text-faint)',
};

const STATUS_LABEL = {
    active: 'Activ',
    trial: 'Trial',
    pending_payment: 'Plată în așteptare',
    suspended: 'Suspendat',
    expired: 'Expirat',
};

/** Dropdown that lets a chain owner switch between their locations. Hidden
 *  for single-location accounts and for non-owner roles. */
export function LocationSwitcher() {
    const { user, restaurant, myLocations, switchLocation } = useAuth();
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(null);
    const ref = useRef(null);

    useEffect(() => {
        if (!open) return;
        const click = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        const esc = (e) => { if (e.key === 'Escape') setOpen(false); };
        window.addEventListener('mousedown', click);
        window.addEventListener('keydown', esc);
        return () => {
            window.removeEventListener('mousedown', click);
            window.removeEventListener('keydown', esc);
        };
    }, [open]);

    if (user?.role !== 'restaurant_admin') {
        // Plain non-clickable name for waiters / kitchen / managers.
        return restaurant?.name
            ? <div className="dash-user-restaurant" data-testid="restaurant-name">{restaurant.name}</div>
            : null;
    }

    // Single-location owners: render plain name + small "Adaugă locație" link
    // to discover the feature.
    const hasMultiple = (myLocations?.length || 0) > 1;
    if (!hasMultiple) {
        return (
            <div data-testid="location-switcher-single">
                {restaurant?.name && (
                    <div className="dash-user-restaurant" data-testid="restaurant-name">{restaurant.name}</div>
                )}
                <Link to="/r/locations" style={{
                    fontSize: '0.75rem', color: 'var(--text-faint)',
                    textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 3,
                    marginTop: 2,
                }} data-testid="location-add-link">
                    <Plus size={11} /> Adaugă locație
                </Link>
            </div>
        );
    }

    const activeId = restaurant?.id;
    const handleSwitch = async (rid) => {
        if (rid === activeId) {
            setOpen(false);
            return;
        }
        setBusy(rid);
        try {
            await switchLocation(rid);
            // switchLocation reloads the page so we won't actually reach the next line
        } catch {
            setBusy(null);
        }
    };

    return (
        <div ref={ref} style={{ position: 'relative' }} data-testid="location-switcher">
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                style={{
                    background: 'transparent',
                    border: '1px solid var(--border)',
                    borderRadius: 8,
                    padding: '0.4rem 0.6rem',
                    cursor: 'pointer',
                    color: 'var(--text)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                    fontSize: '0.88rem',
                    fontWeight: 600,
                    width: '100%',
                    justifyContent: 'space-between',
                    transition: 'border-color 0.15s',
                }}
                data-testid="location-switcher-trigger"
            >
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, overflow: 'hidden' }}>
                    <MapPin size={13} style={{ color: 'var(--amber)', flexShrink: 0 }} />
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {restaurant?.name || '—'}
                    </span>
                </span>
                <ChevronDown size={14} style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s', flexShrink: 0 }} />
            </button>

            {open && (
                <div
                    role="menu"
                    style={{
                        position: 'absolute',
                        top: 'calc(100% + 4px)',
                        left: 0,
                        right: 0,
                        zIndex: 50,
                        background: 'var(--surface)',
                        border: '1px solid var(--border-strong)',
                        borderRadius: 10,
                        boxShadow: '0 12px 36px rgba(0,0,0,0.55)',
                        overflow: 'hidden',
                        minWidth: 240,
                    }}
                    data-testid="location-switcher-menu"
                >
                    {myLocations.map((loc) => {
                        const isActive = loc.id === activeId;
                        return (
                            <button
                                key={loc.id}
                                type="button"
                                onClick={() => handleSwitch(loc.id)}
                                disabled={busy === loc.id}
                                data-testid={`location-option-${loc.id}`}
                                style={{
                                    background: isActive ? 'rgba(242,101,34,0.08)' : 'transparent',
                                    border: 'none',
                                    width: '100%',
                                    padding: '0.65rem 0.8rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 10,
                                    cursor: busy === loc.id ? 'wait' : 'pointer',
                                    textAlign: 'left',
                                    color: 'var(--text)',
                                    fontSize: '0.88rem',
                                    borderBottom: '1px solid var(--border)',
                                    transition: 'background 0.12s',
                                }}
                                onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = 'var(--surface-2)'; }}
                                onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
                            >
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                                        {loc.name}
                                        {loc.is_primary && (
                                            <span style={{
                                                fontSize: '0.65rem',
                                                color: 'var(--amber)',
                                                background: 'rgba(242,101,34,0.12)',
                                                padding: '1px 5px',
                                                borderRadius: 3,
                                                fontWeight: 700,
                                            }}>PRIMARY</span>
                                        )}
                                    </div>
                                    <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 2 }}>
                                        {loc.plan?.toUpperCase()} ·{' '}
                                        <span style={{ color: STATUS_COLOR[loc.status] || 'var(--text-muted)' }}>
                                            {STATUS_LABEL[loc.status] || loc.status}
                                        </span>
                                    </div>
                                </div>
                                {isActive && <Check size={14} style={{ color: 'var(--amber)' }} />}
                            </button>
                        );
                    })}
                    <Link
                        to="/r/locations"
                        onClick={() => setOpen(false)}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            padding: '0.65rem 0.8rem',
                            color: 'var(--amber)',
                            textDecoration: 'none',
                            fontSize: '0.85rem',
                            fontWeight: 600,
                        }}
                        data-testid="location-add-new"
                    >
                        <Plus size={14} /> Adaugă o locație nouă
                    </Link>
                </div>
            )}
        </div>
    );
}
